import systemShareStoreV1 from './store'
import { IStore } from './core/interface/IStore'
import { ISysShareModule } from '@/contracts/system-modules/share/ISysShareModule'
import { ISysAuthModule } from '@/contracts/system-modules/auth/ISysAuthModule'
import { ISysConsoleModule } from '@/contracts/system-modules/console/ISysConsoleModule'
import { ISysModManagerModule } from '@/contracts/system-modules/mod-manager/ISysModManagerModule'
import { ISysWinManagerModule } from '@/contracts/system-modules/win-manager/ISysWinManagerModule'
import { IEventBus } from '@/contracts/core/event/interface/IEventBus'
import { EnumSystemEvent } from '@/contracts/core/event/enum/EnumSystemEvent'
import { EnumConsoleType } from '@/contracts/system-modules/console/enum/EnumConsoleType'
import { EnumViewMode } from '@/contracts/system-modules/share/enum/EnumViewMode'
import { IZone } from '@/contracts/system-modules/share/IZone'
import { IViewer } from '@/contracts/system-modules/share/IViewer'
import { IOwner } from '@/contracts/system-modules/share/IOwner'
import { ILink } from '@/contracts/system-modules/share/ILink'
import { IPublicLink } from '@/contracts/system-modules/share/IPublicLink'
import { ILinkDraft } from '@/contracts/system-modules/share/ILinkDraft'
import { IViewContext } from '@/contracts/system-modules/share/IViewContext'
import { ApiResponse } from '@/contracts/core/api/entity/ApiResponse'
import { EventProviderModule } from '@/core/event/EventProvider'
import { SysAuthProviderModule } from '@/system-modules/auth/SysAuthProvider'
import { SysConsoleProviderModule } from '@/system-modules/console/ConsoleProvider'
import { SysModManagerProviderModule } from '@/system-modules/mod-manager/ModManagerProvider'
import { SysWinManagerProviderModule } from '@/system-modules/win-manager/WinManagerProvider'
import { makeShareLinkUrl } from '@/helpers/shareLink'
import { GetViewersRoute } from '@/system-modules/share/v1/api/v1/share/GetViewers/GetViewersRoute'
import { SetViewerRoute } from '@/system-modules/share/v1/api/v1/share/SetViewer/SetViewerRoute'
import { SetViewerParams } from '@/system-modules/share/v1/api/v1/share/SetViewer/Dto/SetViewerParams'
import { RemoveViewerRoute } from '@/system-modules/share/v1/api/v1/share/RemoveViewer/RemoveViewerRoute'
import { GetOwnersRoute } from '@/system-modules/share/v1/api/v1/share/GetOwners/GetOwnersRoute'
import { GetLinksRoute } from '@/system-modules/share/v1/api/v1/share/GetLinks/GetLinksRoute'
import { CreateLinkRoute } from '@/system-modules/share/v1/api/v1/share/CreateLink/CreateLinkRoute'
import { CreateLinkParams } from '@/system-modules/share/v1/api/v1/share/CreateLink/Dto/CreateLinkParams'
import { RemoveLinkRoute } from '@/system-modules/share/v1/api/v1/share/RemoveLink/RemoveLinkRoute'
import { OpenLinkRoute } from '@/system-modules/share/v1/api/v1/share/OpenLink/OpenLinkRoute'
import { CreateChildLinkRoute } from '@/system-modules/share/v1/api/v1/share/CreateChildLink/CreateChildLinkRoute'
import { CreateChildLinkParams } from '@/system-modules/share/v1/api/v1/share/CreateChildLink/Dto/CreateChildLinkParams'

export class SysShareV1 implements ISysShareModule {
  private static instance: ISysShareModule
  private store: IStore = systemShareStoreV1()
  private events: IEventBus
  private auth: () => ISysAuthModule
  private modManager: () => ISysModManagerModule
  private winManager: () => ISysWinManagerModule
  private console: ISysConsoleModule

  /**
   * Соседние модули берутся при обращении, а не в конструкторе: auth и share нужны друг
   * другу, а менеджеры модулей и окон создаются позже share.
   */
  constructor(
    events: IEventBus = EventProviderModule(),
    auth: () => ISysAuthModule = () => SysAuthProviderModule(),
    modManager: () => ISysModManagerModule = () => SysModManagerProviderModule(),
    winManager: () => ISysWinManagerModule = () => SysWinManagerProviderModule(),
    console: ISysConsoleModule = SysConsoleProviderModule(),
  ) {
    this.events = events
    this.auth = auth
    this.modManager = modManager
    this.winManager = winManager
    this.console = console
  }

  public static getInstance(): ISysShareModule {
    if (!SysShareV1.instance) {
      SysShareV1.instance = new SysShareV1()
    }
    return SysShareV1.instance
  }

  init(): boolean {
    this.events.on(EnumSystemEvent.LOCKED, () => this.leaveViewer())
    return true
  }

  getZones(): IZone[] {
    return Object.values(this.modManager().getAll()).flatMap((module) => module.getMeta().getZones())
  }

  async loadViewers(): Promise<void> {
    const response = await new GetViewersRoute().exec(null)
    if (this._isOk(response, 'viewers')) {
      this.store.viewers = response.getData()
    }
  }

  getViewers(): IViewer[] {
    return this.store.viewers
  }

  async setViewer(viewerUuid: string, zones: string[]): Promise<boolean> {
    const response = await new SetViewerRoute(viewerUuid).exec(new SetViewerParams({ zones }))
    if (!this._isOk(response, 'set viewer')) {
      return false
    }
    await this.loadViewers()

    return true
  }

  async removeViewer(viewerUuid: string): Promise<boolean> {
    const response = await new RemoveViewerRoute(viewerUuid).exec(null)
    if (!this._isOk(response, 'remove viewer')) {
      return false
    }
    await this.loadViewers()

    return true
  }

  async loadOwners(): Promise<void> {
    const response = await new GetOwnersRoute().exec(null)
    if (this._isOk(response, 'owners')) {
      this.store.owners = response.getData()
    }
  }

  getOwners(): IOwner[] {
    return this.store.owners
  }

  async loadLinks(): Promise<void> {
    const response = await new GetLinksRoute().exec(null)
    if (this._isOk(response, 'links')) {
      this.store.links = response.getData()
    }
  }

  getLinks(): ILink[] {
    return this.store.links
  }

  async createLink(draft: ILinkDraft): Promise<ILink | null> {
    if (this.store.mode === EnumViewMode.GUEST) {
      return this._createChildLink(draft)
    }

    const response = await new CreateLinkRoute().exec(new CreateLinkParams({ ...draft, owner: this.getContext().ownerUuid }))
    if (!this._isOk(response, 'create link')) {
      return null
    }
    this.store.links = [...this.store.links, response.getData()]

    return response.getData()
  }

  async removeLink(uuid: string): Promise<boolean> {
    const response = await new RemoveLinkRoute(uuid).exec(null)
    if (!this._isOk(response, 'remove link')) {
      return false
    }
    await this.loadLinks()

    return true
  }

  getLinkUrl(uuid: string): string {
    return makeShareLinkUrl(window.location.origin, uuid)
  }

  getContext(): IViewContext {
    return {
      mode: this.store.mode,
      ownerUuid: this.store.mode === EnumViewMode.OWNER ? this.auth().getUserUuid() : this.store.ownerUuid,
      page: this.store.page,
      linkUuid: this.store.linkUuid,
    }
  }

  enterViewer(ownerUuid: string): void {
    if (this.store.mode === EnumViewMode.GUEST) {
      return
    }
    this.store.mode = EnumViewMode.VIEWER
    this.store.ownerUuid = ownerUuid
    this.store.page = null
    this.store.linkUuid = null
  }

  leaveViewer(): void {
    if (this.store.mode !== EnumViewMode.VIEWER) {
      return
    }
    this.store.mode = EnumViewMode.OWNER
    this.store.ownerUuid = ''
  }

  canView(zone: string): boolean {
    switch (this.store.mode) {
    case EnumViewMode.OWNER:
      return true
    case EnumViewMode.VIEWER:
      return this._ownerZones(this.store.ownerUuid).includes(zone)
    default:
      return zone === this.store.linkZone
    }
  }

  async openGuestLink(linkUuid: string): Promise<void> {
    const response = await new OpenLinkRoute(linkUuid).exec(null)
    if (!this._isOk(response, 'open link ' + linkUuid)) {
      return
    }

    const link = response.getData()
    this.store.mode = EnumViewMode.GUEST
    this.store.ownerUuid = link.ownerUuid
    this.store.page = link.target
    this.store.linkUuid = link.uuid
    this.store.linkZone = link.zone
    this._openModuleWindow(link.module)
  }

  isGuest(): boolean {
    return this.store.mode === EnumViewMode.GUEST
  }

  getViewPath(): string {
    return '/share/v1/view/index.vue'
  }

  async _createChildLink(draft: ILinkDraft): Promise<ILink | null> {
    const parentUuid = this.store.linkUuid ?? ''
    const response = await new CreateChildLinkRoute(parentUuid).exec(new CreateChildLinkParams({ expiresAt: draft.expiresAt }))
    if (!this._isOk(response, 'share link again')) {
      return null
    }

    return this._childToLink(response.getData(), parentUuid)
  }

  /** Гостю сервер отдаёт короткую форму ссылки: ответственного и просмотров в ней нет */
  _childToLink(link: IPublicLink, parentUuid: string): ILink {
    return {
      uuid: link.uuid,
      ownerUuid: link.ownerUuid,
      creatorUuid: null,
      responsibleUuid: '',
      parentUuid,
      module: link.module,
      target: link.target,
      zone: link.zone,
      expiresAt: link.expiresAt,
      viewCount: 0,
      lastViewedAt: null,
      removedAt: null,
    }
  }

  _ownerZones(ownerUuid: string): string[] {
    return this.store.owners.find((owner) => owner.ownerUuid === ownerUuid)?.zones ?? []
  }

  _openModuleWindow(moduleCode: string): void {
    const module = Object.values(this.modManager().getAll()).find((item) => item.getMeta().getCode() === moduleCode)
    if (!module) {
      this.console.log(`Share: module ${moduleCode} is not installed`, EnumConsoleType.ERROR)
      return
    }
    this.winManager().openWin(module.getMeta().getModuleId())
  }

  _isOk<T>(response: ApiResponse<T>, action: string): boolean {
    if (response.isSuccess()) {
      return true
    }
    this.console.log(`Share: ${action} failed: ${response.getError()}`, EnumConsoleType.ERROR)

    return false
  }
}

export default function (): ISysShareModule {
  return SysShareV1.getInstance()
}
